import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withRouter } from 'react-router-dom';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';

import useOpenModal from '../../../../../../../hooks/useOpenModal';
import usePendingDigitalBundles from './hooks/usePendingDigitalBundles';
import DigitalBundlesMissingConfigurationModal, {
  DIGITAL_BUNDLES_MISSING_CONFIGURATION_MODAL,
} from './components/DigitalBundlesMissingConfigurationModal';
import {
  getAlbumMembershipGroupEntity,
  isMultiIdentityBundleMembership,
} from '../../../../../../temp/views/memberships/AlbumMembershipGroup/helpers';
import { isAlbumFolder } from '../../../../../../../util/album-folder';
import { getPurchasedCustomPrintForConfigQuery } from '../../../../../../temp/views/memberships/AlbumMembershipGroup/graphql/queries/getPurchasedCustomPrintsForConfig';
import DigitalBundleConfigurationCompleteModal from './components/DigitalBundleConfigurationCompleteModal/modal';

const propTypes = {
  identity: PropTypes.shape({
    id: PropTypes.string,
  }),
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string,
  }),
  matchedPhotosTotal: PropTypes.number,
  purchasedCustomPrint: PropTypes.shape({
    id: PropTypes.string,
  }),
  isAllPhotos: PropTypes.bool,
  className: PropTypes.string,
};

const DigitalBundlesMissingConfigurationNotice = ({
  identity,
  albumMembershipGroup,
  matchedPhotosTotal,
  purchasedCustomPrint,
  isAllPhotos,
  className,
}) => {
  const openModal = useOpenModal();
  const [configurationPopupSeen, setConfigurationPopupSeen] = useState(false);
  const entity = getAlbumMembershipGroupEntity(albumMembershipGroup);

  const { purchasedDigitalBundles } = usePendingDigitalBundles({
    identityId: identity?.id,
    albumFolderId: isAlbumFolder(entity)
      ? entity?.id
      : entity?.root_album_folder?.id,
    pendingConfigurationOnly:
      isAllPhotos || isMultiIdentityBundleMembership(albumMembershipGroup),
  });

  useEffect(() => {
    if (configurationPopupSeen) {
      return;
    }

    const bundleWithoutPhotos = purchasedDigitalBundles.find(
      bundle =>
        bundle.photos?.total === 0 &&
        (bundle.identity?.id === identity?.id ||
          bundle.digital_bundle?.is_multi_identity),
    );

    if (bundleWithoutPhotos && matchedPhotosTotal > 0) {
      openModal(DIGITAL_BUNDLES_MISSING_CONFIGURATION_MODAL);
      setConfigurationPopupSeen(true);
    }
  }, [purchasedDigitalBundles, matchedPhotosTotal]);

  const classes = classNames({
    'digital-bundles-missing-configuration-notice': true,
    'with-purchased-custom-print-notice': !!purchasedCustomPrint,
    'd-none': (purchasedDigitalBundles ?? []).length === 0,
    [className]: !!className,
  });

  return (
    <Fragment>
      <a
        href="#"
        onClick={event => {
          event.preventDefault();
          openModal(DIGITAL_BUNDLES_MISSING_CONFIGURATION_MODAL);
        }}
        className={classes}
      >
        <div className="d-flex align-items-center w-100">
          <span className="d-block mr-1">
            Your{' '}
            <span className="text-extra-bold text-primary">
              Digital Photo Bundle
            </span>{' '}
            has additional photos available for selection.
          </span>

          <span className="d-block text-extra-bold ml-1 text-sm text-primary">
            Select more photos &rarr;
          </span>
        </div>
      </a>

      <DigitalBundlesMissingConfigurationModal
        isAllPhotos={isAllPhotos}
        identity={identity}
        albumMembershipGroup={albumMembershipGroup}
      />
      <DigitalBundleConfigurationCompleteModal
        albumMembershipGroup={albumMembershipGroup}
      />
    </Fragment>
  );
};

const withPurchasedCustomPrint = graphql(
  getPurchasedCustomPrintForConfigQuery,
  {
    options: ({ match }) => ({
      variables: {
        id: match.params.hierarchyItemId,
      },
      fetchPolicy: 'cache-only',
    }),
    props: ({ data: { albumMembershipGroup } }) => ({
      purchasedCustomPrint:
        albumMembershipGroup?.purchased_custom_prints?.records?.[0],
    }),
    skip: ({ match }) => !match.params.hierarchyItemId,
  },
);

DigitalBundlesMissingConfigurationNotice.propTypes = propTypes;

export default compose(
  withRouter,
  withPurchasedCustomPrint,
)(DigitalBundlesMissingConfigurationNotice);
